import React, { useRef } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FILE_TTL_OPTIONS, FileTtl } from '@/hooks/useFiles';
import { cn } from '@/lib/utils';

const TTL_LABELS: Record<FileTtl, string> = {
  '7d': '7 days',
  '14d': '14 days',
  '30d': '30 days',
  '90d': '90 days',
};

// Short labels for the segmented pill variant (the inline group is space-
// constrained, so it mirrors FILE_TTL_OPTIONS verbatim instead of "7 days").
const TTL_SHORT_LABELS: Record<FileTtl, string> = {
  '7d': '7d',
  '14d': '14d',
  '30d': '30d',
  '90d': '90d',
};

// Radix Select can't represent "no value" as an item value, so "keep
// forever" gets a sentinel that maps back to undefined.
const NEVER = 'never';

// Ordered options shared by the segmented variant: "Keep forever" (undefined)
// first, then the fixed durations from FILE_TTL_OPTIONS.
const SEGMENTED_OPTIONS: ReadonlyArray<{
  value: FileTtl | undefined;
  label: string;
}> = [
  { value: undefined, label: 'Keep forever' },
  ...FILE_TTL_OPTIONS.map(ttl => ({
    value: ttl,
    label: TTL_SHORT_LABELS[ttl],
  })),
];

interface FileTtlSelectProps {
  value: FileTtl | undefined;
  onChange: (ttl: FileTtl | undefined) => void;
  className?: string;
  /**
   * Rendering style. `'dropdown'` (default) keeps the Radix Select; `'segmented'`
   * renders an inline horizontal pill group. The dropdown is the canonical form
   * control (Constantine zip-target uploader); segmented suits a contained panel.
   */
  variant?: 'dropdown' | 'segmented';
  /**
   * id of an external label for the segmented group's `aria-labelledby`. Falls
   * back to `aria-label="Auto-delete"` when omitted.
   */
  labelId?: string;
}

/**
 * Retention picker for file uploads. The duration options mirror the
 * backend's fixed TTL enum (S3 lifecycle rules are static, so arbitrary
 * durations aren't supported); "Keep forever" uploads without expiry.
 */
export const FileTtlSelect: React.FC<FileTtlSelectProps> = ({
  value,
  onChange,
  className,
  variant = 'dropdown',
  labelId,
}) => {
  if (variant === 'segmented') {
    return (
      <SegmentedFileTtl
        value={value}
        onChange={onChange}
        className={className}
        labelId={labelId}
      />
    );
  }

  return (
    <Select
      value={value ?? NEVER}
      onValueChange={v => onChange(v === NEVER ? undefined : (v as FileTtl))}
    >
      <SelectTrigger className={className} aria-label="Auto-delete">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NEVER}>Keep forever</SelectItem>
        {FILE_TTL_OPTIONS.map(ttl => (
          <SelectItem key={ttl} value={ttl}>
            Delete after {TTL_LABELS[ttl]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/**
 * Inline segmented pill group. Roving tabindex: only the selected pill (or the
 * first when nothing is selected) is in the tab order; ArrowLeft/ArrowRight
 * move and select within the group. Active state is conveyed by fill AND
 * font-weight (not color alone) for a11y.
 */
const SegmentedFileTtl: React.FC<
  Pick<FileTtlSelectProps, 'value' | 'onChange' | 'className' | 'labelId'>
> = ({ value, onChange, className, labelId }) => {
  const groupRef = useRef<HTMLDivElement | null>(null);

  const selectedIndex = SEGMENTED_OPTIONS.findIndex(o => o.value === value);

  function moveSelection(delta: number) {
    const root = groupRef.current;
    const base = selectedIndex === -1 ? 0 : selectedIndex;
    const nextIndex =
      (base + delta + SEGMENTED_OPTIONS.length) % SEGMENTED_OPTIONS.length;
    onChange(SEGMENTED_OPTIONS[nextIndex].value);
    if (root) {
      const buttons = Array.from(
        root.querySelectorAll<HTMLButtonElement>('button')
      );
      buttons[nextIndex]?.focus();
    }
  }

  return (
    <div
      ref={groupRef}
      role="group"
      {...(labelId
        ? { 'aria-labelledby': labelId }
        : { 'aria-label': 'Auto-delete' })}
      className={cn(
        'border-chariot-stroke bg-chariot-background-primary inline-flex items-center gap-1 rounded-md border p-1',
        className
      )}
    >
      {SEGMENTED_OPTIONS.map((opt, i) => {
        const selected = opt.value === value;
        const isTabStop = selected || (selectedIndex === -1 && i === 0);
        return (
          <button
            key={opt.value ?? NEVER}
            type="button"
            aria-pressed={selected}
            tabIndex={isTabStop ? 0 : -1}
            onClick={() => onChange(opt.value)}
            onKeyDown={e => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                moveSelection(1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                moveSelection(-1);
              }
            }}
            className={cn(
              'focus-visible:ring-chariot-brand-primary h-7 rounded px-3 text-xs outline-hidden transition-colors focus-visible:ring-2',
              selected
                ? 'bg-chariot-brand-primary font-medium text-white'
                : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};
